import type { IMatchingSearchState } from "./matching-search.types";

import { ActionTree } from "vuex";

import { RootState } from "@/services/store/root-state";
import { getBaseName } from "@/services/store/utils/get-base-name";
import { EMatchingSearchActions } from "./matching-search.types";
import { matchingSearchProvider } from "@/services/data/matching-search/matching-search.provider";
import { EMatchingSearchMutations } from "./matching-search.types";

const generateFilters = (
  filters: any,
  page: number,
  per_page: number,
  company_type: number,
) => {
  let filter_by = `type:${company_type !== 0 ? "!" : ""}=supporter`;

  if (filters.sectors?.length > 0) {
    // filter_by = filters.sectors
    //   .map((sec: string) => `sectors:=${sec}`)
    //   .join("");
    filter_by = `sectors:[${filters.sectors.join(",")}]`;
  }

  if (filters.countries?.length > 0) {
    if (filter_by) {
      filter_by += ` && `;
    }
    // filter_by += filters.countries
    //   .map((c: string) => `countries:=${c}`)
    //   .join(" && ");
    filter_by += `countries:[${filters.countries.join(",")}]`;
  }

  if (company_type !== 0 && filters.vil?.length > 0) {
    if (filter_by) {
      filter_by += ` && `;
    }
    filter_by += `vil:[${filters.vil.join("..")}]`;
  }

  if (filters.founding_date?.length > 0) {
    if (filter_by) {
      filter_by += ` && `;
    }

    const sorted = filters.founding_date.sort((a: number, b: number) => a - b);
    if (sorted.length === 1) {
      filter_by += `founding_date:[${sorted[0]}]`;
    } else {
      filter_by += `founding_date:[${sorted[0] + ".." + sorted[sorted.length - 1]}]`;
    }

    if (filters.founding_date && !filters.founding_date.includes("0")) {
      filter_by += " && founding_date:!=[0]";
    }
  }

  const sort_by = `latest_assessment:${filters.sort}`;

  return {
    filter: filter_by,
    query: filters.query,
    sort_by,
    page: page,
    per_page: per_page,
  };
};

export const actions: ActionTree<IMatchingSearchState, RootState> = {
  async [getBaseName(EMatchingSearchActions.LOAD_FACETS)]({ commit }) {
    try {
      commit(EMatchingSearchMutations.SET_LOADING, true);
      commit(EMatchingSearchMutations.SET_FACETS, []);
      commit(EMatchingSearchMutations.SET_ERROR, null);
      const results = await matchingSearchProvider.facets();

      const facets: any = {};
      for (const facet of results) {
        facets[facet.field_name] = {
          facet: facet.field_name,
          options: facet.counts.map((count: any) => ({
            value: count.value,
            label: count.highlighted,
            count: count.count,
          })),
        };
      }

      commit(EMatchingSearchMutations.SET_FACETS, facets);
      commit(EMatchingSearchMutations.SET_LOADING, false);
    } catch (error: any) {
      commit(EMatchingSearchMutations.SET_ERROR, error.message);
      commit(EMatchingSearchMutations.SET_LOADING, false);
    }
  },

  async [getBaseName(EMatchingSearchActions.SEARCH)](
    { state, commit, rootState },
    action: string,
  ) {
    try {
      commit(EMatchingSearchMutations.SET_LOADING, true);
      commit(EMatchingSearchMutations.SET_ERROR, null);
      const results = await matchingSearchProvider.search(
        generateFilters(
          state?.filters,
          action === "scroll" ? state.page + 1 : 1,
          state.per_page,
          rootState.auth.company.data?.type || 0,
        ),
      );

      commit(EMatchingSearchMutations.SET_RESULTS, {
        documents: results.hits.map((hit: any) => hit.document),
        found: results.found,
        page: results.page,
        per_page: results.per_page,
        action: action,
      });

      commit(EMatchingSearchMutations.SET_LOADING, false);
      return results.page * results.request_params.per_page < results.found;
    } catch (error: any) {
      commit(EMatchingSearchMutations.SET_ERROR, error.message);
      commit(EMatchingSearchMutations.SET_LOADING, false);
      return false;
    }
  },

  async [getBaseName(EMatchingSearchActions.SET_FILTERS)](
    { state, commit, dispatch },
    {
      filter,
      action,
      value,
    }: {
      filter: keyof typeof state.filters | "all";
      action: string;
      value: any;
    },
  ) {
    const arrays = ["countries", "sectors", "founding_date"];
    const resetAllFilters = filter === "all" && action === "clear";
    const resetSpecificFilters = filter !== "all" && action === "clear";
    const updateSpecificFilter = filter !== "all" && action === "update";
    const selectAllSpecificFilters = filter !== "all" && action === "all";

    if (resetAllFilters) {
      commit(EMatchingSearchMutations.SET_FILTERS, {
        countries: [],
        regions: [],
        sectors: [],
        vil: [1, 6],
        action: "search",
        query: "",
        sort: "desc",
        founding_date: [],
        sectorsFacetQuery: "",
        countriesFacetQuery: "",
        foundingDateFacetQuery: "",
      });
    }

    if (resetSpecificFilters) {
      commit(EMatchingSearchMutations.SET_FILTERS, {
        ...state.filters,
        [filter]: value,
      });
    }

    if (updateSpecificFilter) {
      const currentSelections = state.filters[filter] as any[];
      let updatedSelections: any = [];

      if (arrays.includes(filter)) {
        updatedSelections = currentSelections.includes(value)
          ? currentSelections.filter((s: string | number) => s !== value)
          : [...currentSelections, value];
      } else {
        updatedSelections = value;
      }

      commit(EMatchingSearchMutations.SET_FILTERS, {
        ...state.filters,
        [filter]: updatedSelections,
      });
    }

    if (selectAllSpecificFilters) {
      if (arrays.includes(filter)) {
        commit(EMatchingSearchMutations.SET_FILTERS, {
          ...state.filters,
          // @ts-expect-error fixe types
          [filter]: state.facets[filter].options.map((item: any) => item.value),
        });
      }
    }

    if (
      // search is triggered directly from the filters page
      filter !== "query" &&
      // these trigger facet search not document search
      filter !== "sectorsFacetQuery" &&
      filter !== "countriesFacetQuery" &&
      filter !== "foundingDateFacetQuery"
    ) {
      dispatch(EMatchingSearchActions.SEARCH, "search", { root: true });
    }
  },

  async [getBaseName(EMatchingSearchActions.SEARCH_FILTERS)](
    { state, commit },
    { facet, query },
  ) {
    commit(EMatchingSearchMutations.SET_LOADING_FACET, true);
    const results = await matchingSearchProvider.searchFacet(
      facet,
      `${facet}:${query}`,
    );
    commit(EMatchingSearchMutations.SET_FACETS, {
      ...state.facets,
      [facet]: {
        facet: facet,
        options: results.counts.map((count: any) => ({
          value: count.value,
          label: count.highlighted,
          count: count.count,
        })),
      },
    });
    commit(EMatchingSearchMutations.SET_LOADING_FACET, false);
  },
};
